import { OAuthService } from 'angular-oauth2-oidc';
import { API_BASE } from '../../constants/general.constants';
import { AppStateService } from '../app-state/app-state.service';

/**
 * Bridge service for EHQ.call() in browser Python steps.
 *
 * All service calls proxy through ehq-server, which resolves the
 * connection's URL and applies auth credentials. The browser never
 * calls services directly.
 */
export class ServiceCallBridgeService {
  constructor(
    private readonly oauth: OAuthService,
    private readonly appState: AppStateService,
  ) {}

  /**
   * Called by the Python bridge as EHQ.call(connectionName, method, args).
   *
   * @param connectionName - Project connection name (e.g., "echo-svc")
   * @param method - Service method to invoke (e.g., "echo")
   * @param args - Arguments to pass as JSON body (default: {})
   * @returns Parsed JSON response from the service
   */
  async call(connectionName: string, method: string, args: Record<string, unknown> = {}): Promise<unknown> {
    const projectId = this.appState.projectId();
    if (!projectId) {
      throw new Error('EHQ.call: no active project');
    }

    const token = this.oauth.getAccessToken();
    const url = `${API_BASE}/project/${projectId}/service-call/${encodeURIComponent(connectionName)}/${encodeURIComponent(method)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(args),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Service ${connectionName}/${method} failed: ${response.status} ${text}`);
    }

    return await response.json();
  }
}
